/**
 * The "Rechnungstexte" section of /admin/bexio/settings (D4.1a).
 *
 * PRESENTATIONAL ONLY, like every other section under `settings/`: it renders what
 * the page hands it and fetches nothing.
 *
 * TWO FIELDS, TWO DIFFERENT KINDS OF SETTING — that asymmetry is the whole point
 * of this section, not an oversight:
 *
 * - HEADER (`default_header_text`) is read by exactly one consumer, the Bexio draft
 *   payload builder. Bexio owns it, so it is EDITED here.
 * - FOOTER (`default_footer_text`) is read by four document types — the Bexio
 *   invoice, the native invoice PDF, the handover protocol and the key receipt. It
 *   is a shared document setting that merely happens to also reach Bexio, so it is
 *   only DISPLAYED here and stays editable on the cross-document billing-settings
 *   page. Making it editable in both places would create two edit surfaces over one
 *   stored value: whoever saved last would silently overwrite the other, and the
 *   handover protocol would change because someone was editing "Bexio settings".
 *
 * Both live in the SAME database row and are served by the SAME endpoint. Only the
 * edit surface differs, and only because the consumer sets differ.
 *
 * WHY THE FOOTER IS NOT A DISABLED TEXTAREA. A greyed-out form control reads as a
 * temporary state — "log in", "wait", "fix something" — and invites the operator to
 * hunt for the unlock. This value is not pending; it is simply maintained elsewhere.
 * So it is rendered as text with an explanatory line, the same way the read-only
 * config fields on this page are.
 *
 * NO PLACEHOLDERS (D4 contract). Neither field expands `[Name]`, `{{name}}`, a
 * recipient or a salutation, and the hints must never suggest otherwise: the text is
 * stored and sent verbatim. Bexio's own stock texts contain such tokens, which is
 * exactly why silence here would be read as support.
 */
import React from "react";

const CARD = "rounded-xl border border-border bg-card p-5";
const CARD_TITLE = "text-sm font-semibold text-foreground";
const LABEL = "mb-1 block text-xs font-medium text-foreground";
const HINT = "mt-1 text-xs text-muted-foreground";
const TEXTAREA =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "disabled:cursor-not-allowed disabled:opacity-60";
const BUTTON =
  "rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "disabled:cursor-not-allowed disabled:opacity-60";

export default function BexioInvoiceTexts({
  loading = false,
  error = "",
  onRetry,
  headerDraft = "",
  footerText = null,
  canEdit = false,
  dirty = false,
  saving = false,
  saveError = "",
  onHeaderChange = () => {},
  onSave = () => {},
}) {
  const saveErrorId = "bexio-invoice-texts-save-error";
  const footerValue = footerText == null ? "" : String(footerText);

  return (
    <section aria-label="Rechnungstexte" className={`${CARD} mt-4`}>
      <h2 className={CARD_TITLE}>Rechnungstexte</h2>

      {error ? (
        // Isolated on purpose: this section reaches a DIFFERENT endpoint from the
        // rest of the page. Its failure must not blank the provider configuration
        // the operator came here to read, so it reports inside its own card and
        // offers its own retry.
        <div className="mt-3">
          <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">
            {error}
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      ) : loading ? (
        <p className="mt-3 text-sm text-muted-foreground">Wird geladen …</p>
      ) : (
        <div className="mt-3 space-y-5">
          {/* ── Header: the one field Bexio owns, and the only one editable here ── */}
          <div>
            <label htmlFor="bexio-default_header_text" className={LABEL}>
              Standard-Kopfzeile
            </label>
            {canEdit ? (
              <textarea
                id="bexio-default_header_text"
                rows={6}
                maxLength={5000}
                value={headerDraft}
                onChange={(e) => onHeaderChange(e.target.value)}
                disabled={saving}
                aria-describedby={saveError ? saveErrorId : undefined}
                className={TEXTAREA}
              />
            ) : (
              <p className="whitespace-pre-line text-sm text-foreground">
                {headerDraft || "Nicht konfiguriert"}
              </p>
            )}
            <p className={HINT}>
              Wird standardmässig als Einleitung auf Bexio-Rechnungen verwendet.
            </p>

            {canEdit ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={onSave}
                  // Its own control, not the page's generic "Änderungen speichern".
                  // That one writes Bexio provider config through
                  // `PATCH /api/admin/bexio/config`; this writes the billing-settings
                  // row through a different endpoint. One button spanning two
                  // endpoints would mean a partial failure with no honest thing to
                  // report. Guarded on `dirty` as well as disabled, because a
                  // re-render can re-enable a button between click and request.
                  disabled={!dirty || saving}
                  aria-describedby={saveError ? saveErrorId : undefined}
                  className={BUTTON}
                >
                  {/* A distinct accessible name: this page already has
                      "Änderungen speichern" and "Ansprechpartner speichern". */}
                  {saving ? "Speichert Kopfzeile …" : "Kopfzeile speichern"}
                </button>
                {saveError ? (
                  <span
                    id={saveErrorId}
                    role="alert"
                    className="mt-1 block text-xs text-amber-800 dark:text-amber-300"
                  >
                    {saveError}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* ── Footer: shown, never edited here. See the module docstring. ── */}
          <div>
            <p className={LABEL}>Standard-Fusszeile</p>
            <p
              data-testid="bexio-default_footer_text"
              className="whitespace-pre-line text-sm text-foreground"
            >
              {footerValue || "Nicht konfiguriert"}
            </p>
            <p className={HINT}>
              Wird zentral für mehrere Dokumenttypen verwendet und hier nur
              angezeigt. Die Pflege erfolgt in den Rechnungseinstellungen.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
